// La FICHE D'ENTRETIEN, ses trois blocs ajoutés par la migration 054 :
//
//   · Rendez-vous — programmer la séance (même invitation que dans l'étude) ;
//   · Importer    — une source (lien YouTube, fichier, texte), les formats à
//                   générer, le ton ; puis « Générer mon contenu » ;
//   · Synthèses   — ce qui a été généré, prêt à copier.
import { useState, type ChangeEvent, type ReactNode } from 'react';
import { ApiRequestError } from '../api/client';
import { useImportInterview } from '../api/queries';
import type { Interview, InterviewOutput, InterviewOutputFormat, InterviewTone } from '../api/types';
import { googleCalendarLink } from '../calendar/api';
import { Button, buttonClass, Chip, ErrorBanner, Field, Note, Select, CopyButton } from '../components/ui';
import { Icon, type IconName } from '../components/icons';
import { openTab } from '../components/Tabs';
import { InviteForm } from '../invitations/StudyInvitationsSection';
import { PROVIDER_LABELS } from '../invitations/api';
import { Link } from '../router';
import { TextArea } from '../write/Forms';
import './interviews.css';

const errorOf = (cause: unknown) =>
  cause instanceof ApiRequestError ? cause.message : 'Une erreur inattendue est survenue.';

export const FORMATS: { value: InterviewOutputFormat; title: string; text: string; icon: IconName }[] = [
  { value: 'transcription', title: 'Transcription copy-ready', text: 'Transcription nettoyée', icon: 'text' },
  { value: 'synthese', title: 'Synthèse rapide', text: 'Points clés essentiels', icon: 'checklist' },
  { value: 'objections', title: 'Objections & verbatims', text: 'Douleurs, freins & citations', icon: 'quote' },
  { value: 'ux', title: 'Synthèse d’entretien UX', text: 'Verbatims classés par thème', icon: 'audit' },
];

export const TONES: { value: InterviewTone; label: string }[] = [
  { value: 'neutre', label: 'Neutre' },
  { value: 'professionnel', label: 'Professionnel' },
  { value: 'decontracte', label: 'Décontracté' },
  { value: 'pedagogique', label: 'Pédagogique' },
];

const formatOf = (f: InterviewOutputFormat) => FORMATS.find((x) => x.value === f)!;

function slotText(iso: string) {
  return new Date(iso).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// RENDEZ-VOUS
// ---------------------------------------------------------------------------

export function InterviewAppointment({ projectId, interview: it, canWrite }: { projectId: string; interview: Interview; canWrite: boolean }) {
  const studyPath = `/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(it.study_id)}`;
  // Programmer se fait DANS la page, pas dans un panneau latéral : on voit la
  // fiche et le formulaire ensemble. Sans rendez-vous, le formulaire est là
  // d'emblée ; avec un rendez-vous, il se déplie à la demande.
  const [scheduling, setScheduling] = useState(false);
  const inviteForm = (
    <div className="itw-schedule">
      <InviteForm
        projectId={projectId}
        studyId={it.study_id}
        interviewId={it.id}
        defaultName={it.participant}
        studyLabel={it.study_objective ?? it.study_code ?? ''}
        defaultSubject="Échange sur votre usage"
        onDone={() => setScheduling(false)}
      />
    </div>
  );

  if (!it.invitation_id) {
    return (
      <section className="test-block">
        <h2 className="t-h3">Programmer le rendez-vous</h2>
        <p className="card-note">
          Choisissez la personne (dans l’annuaire ou à la main) et proposez un ou plusieurs créneaux. Elle choisit
          sur son lien personnel ; à distance, le lien Meet ou Teams est créé à son accord.
        </p>
        {canWrite ? inviteForm : <p className="zy-texte-muet">Aucun rendez-vous programmé.</p>}
      </section>
    );
  }
  const schedule = canWrite ? (
    scheduling ? null : (
      <Button variant="ghost" onClick={() => setScheduling(true)}>Programmer un autre rendez-vous</Button>
    )
  ) : null;

  const status = it.invitation_status;
  const remote = it.invitation_mode !== 'onsite';
  return (
    <section className="test-block">
      <div className="block-head">
        <h2 className="t-h3">Rendez-vous</h2>
        {status === 'accepted' ? <Chip state="succes">Confirmé</Chip>
          : status === 'declined' ? <Chip state="erreur">Décliné</Chip>
            : <Chip state="avertissement">En attente de réponse</Chip>}
      </div>
      <dl className="facts">
        <div>
          <dt>Quand</dt>
          <dd>
            {it.invitation_starts_at
              ? slotText(it.invitation_starts_at)
              : `À choisir par la personne, parmi ${it.invitation_slots?.length ?? 0} créneaux`}
            {it.invitation_duration_min ? ` · ${it.invitation_duration_min} min` : ''}
          </dd>
        </div>
        <div>
          <dt>Où</dt>
          <dd>
            {remote
              ? (it.invitation_provider ? PROVIDER_LABELS[it.invitation_provider] : 'Visioconférence')
              : `Sur place : ${it.invitation_location ?? ''}`}
          </dd>
        </div>
        {it.invitation_invitee ? (
          <div>
            <dt>Invité·e</dt>
            <dd>{it.invitation_invitee}</dd>
          </div>
        ) : null}
      </dl>
      <div className="itw-actions">
        {it.invitation_meeting_url ? (
          <a className={buttonClass('accent')} href={it.invitation_meeting_url} target="_blank" rel="noreferrer noopener">
            Rejoindre la visioconférence
          </a>
        ) : remote && status === 'accepted' ? (
          <span className="zy-texte-muet">Le lien de la visioconférence s’ajoute depuis l’onglet Participants de l’étude.</span>
        ) : null}
        {status === 'accepted' && it.invitation_starts_at ? (
          <a
            className={buttonClass('ghost')}
            href={googleCalendarLink({
              title: `Entretien — ${it.participant}`,
              startsAt: it.invitation_starts_at,
              durationMin: it.invitation_duration_min ?? 45,
              details: it.invitation_meeting_url ?? it.invitation_location ?? '',
            })}
            target="_blank"
            rel="noreferrer noopener"
          >
            Ajouter à Google Agenda
          </a>
        ) : null}
        <Link to={studyPath} className="card-title-link">Voir toutes les invitations de l’étude</Link>
      </div>
      {schedule}
      {scheduling ? inviteForm : null}
      <p className="zy-texte-muet">
        Après la séance : importez l’enregistrement ou la transcription dans l’onglet{' '}
        <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => openTab('entretien', 'importer')}>Importer</Button>.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// IMPORTER
// ---------------------------------------------------------------------------

type Source = 'enregistrer' | 'youtube' | 'fichier' | 'texte';

const SOURCES: { value: Source; label: string; icon: IconName }[] = [
  // Sur téléphone seulement (`captureSlot` fourni) : enregistrer sur place.
  { value: 'enregistrer', label: 'Enregistrer', icon: 'mic' },
  { value: 'youtube', label: 'YouTube', icon: 'video' },
  { value: 'fichier', label: 'Fichier', icon: 'upload' },
  { value: 'texte', label: 'Texte', icon: 'text' },
];

const TEXT_FILE = /\.(txt|vtt|srt|md)$/i;

export function InterviewImport({
  projectId,
  interview: it,
  importer,
  recordingSlot,
  captureSlot,
}: {
  projectId: string;
  interview: Interview;
  importer: { youtube: boolean; generate: boolean };
  /** L'envoi d'un fichier AUDIO ou VIDÉO (enregistrement, puis transcription). */
  recordingSlot: ReactNode;
  /** Mode mobile : enregistrer la voix ou filmer avec le téléphone. */
  captureSlot?: ReactNode;
}) {
  const run = useImportInterview(projectId);
  const transcribed = it.transcript_status === 'done';
  const [source, setSource] = useState<Source>(captureSlot ? 'enregistrer' : importer.youtube ? 'youtube' : 'fichier');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [formats, setFormats] = useState<InterviewOutputFormat[]>(['synthese', 'objections', 'ux']);
  const [tone, setTone] = useState<InterviewTone>('neutre');
  const [done, setDone] = useState<string | null>(null);

  const toggle = (f: InterviewOutputFormat) =>
    setFormats(formats.includes(f) ? formats.filter((x) => x !== f) : [...formats, f]);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile({ name: f.name, text: await f.text() });
  }

  const hasSource = transcribed
    || (source === 'youtube' && url.trim() !== '')
    || (source === 'texte' && text.trim() !== '')
    || (source === 'fichier' && file !== null);
  const ready = hasSource && (formats.length > 0 || !transcribed) && (formats.length === 0 || importer.generate);

  async function generate() {
    // « Enregistrer » envoie l'enregistrement à la transcription : rien à générer ici.
    if (source === 'enregistrer') return;
    setDone(null);
    try {
      const res = await run.mutateAsync({
        id: it.id,
        source: transcribed ? 'transcription' : source,
        ...(source === 'youtube' && !transcribed ? { url: url.trim() } : {}),
        ...(source === 'texte' && !transcribed ? { text } : {}),
        ...(source === 'fichier' && file && !transcribed ? { text: file.text, fileName: file.name } : {}),
        formats,
        tone,
      });
      setDone(res.generated.length
        ? `${res.generated.length} contenu${res.generated.length > 1 ? 's' : ''} généré${res.generated.length > 1 ? 's' : ''}.`
        : 'Transcription importée.');
      openTab('entretien', res.generated.length ? 'syntheses' : 'importer');
    } catch {
      /* l'erreur est affichée sous le bouton */
    }
  }

  return (
    <section className="test-block itw-import">
      <h2 className="t-h3">Importer un entretien</h2>
      <p className="card-note">Transformez une vidéo, un audio ou un texte en synthèses prêtes à partager.</p>

      <div className="zy-field">
        <span className="zy-field__label">Source</span>
        {transcribed ? (
          <p className="itw-source-done">
            <Icon name="text" size={16} /> La transcription de l’entretien est déjà là : les contenus partent d’elle.
          </p>
        ) : (
          <div className="itw-sources" role="radiogroup" aria-label="Source">
            {/* YouTube n'apparaît que si la clé Supadata est saisie au back-office. */}
            {SOURCES.filter((s) => (s.value !== 'youtube' || importer.youtube) && (s.value !== 'enregistrer' || captureSlot)).map((s) => (
              <button
                key={s.value}
                type="button"
                role="radio"
                aria-checked={source === s.value}
                className={`itw-source${source === s.value ? ' itw-on' : ''}`}
                onClick={() => setSource(s.value)}
              >
                <Icon name={s.icon} size={18} />
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {!transcribed && source === 'enregistrer' && captureSlot ? captureSlot : null}

      {!transcribed && source === 'youtube' && importer.youtube ? (
        <Field
          label="Lien YouTube"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=…"
          hint="Récupération instantanée du texte via Supadata (sous-titres classiques et auto-générés). Aucune vidéo n’est téléchargée."
        />
      ) : null}

      {!transcribed && source === 'fichier' ? (
        <div className="itw-file">
          <label className="zy-field">
            <span className="zy-field__label">Transcription ou sous-titres</span>
            <input className="zy-input" type="file" accept=".txt,.vtt,.srt,.md,text/plain,text/vtt" onChange={(e) => void onFile(e)} />
            <span className="zy-field__aide">
              {file
                ? `${file.name} · ${file.text.length.toLocaleString('fr-FR')} caractères${TEXT_FILE.test(file.name) ? '' : ' (format inhabituel : vérifiez le texte)'}`
                : 'Texte (.txt) ou sous-titres (.vtt, .srt) : les temps et les locuteurs sont gardés.'}
            </span>
          </label>
          <div className="itw-or">ou un fichier audio / vidéo, transcrit par l’IA :</div>
          {recordingSlot}
        </div>
      ) : null}

      {!transcribed && source === 'texte' ? (
        <TextArea
          label="Transcription collée"
          value={text}
          onChange={setText}
          rows={8}
          placeholder={'[00:00] Enquêteur : Comment préparez-vous votre journée ?\n[00:06] Karim : Je commence par vérifier les réservations…'}
          hint="« [mm:ss] Locuteur : texte », « Locuteur : texte » ou de simples paragraphes."
        />
      ) : null}

      {/* Enregistrer sur place : l'enregistrement part à la transcription. Les
          synthèses se génèrent ensuite, depuis la transcription. */}
      {!transcribed && source === 'enregistrer' ? null : (
      <>
      <div className="zy-field">
        <span className="zy-field__label">Formats de sortie</span>
        <div className="itw-formats">
          {FORMATS.map((f) => {
            const on = formats.includes(f.value);
            return (
              <label key={f.value} className={`itw-format${on ? ' itw-on' : ''}`}>
                <Icon name={f.icon} size={20} />
                <span className="itw-format-text">
                  <strong>{f.title}</strong>
                  <span>{f.text}</span>
                </span>
                <input className="zy-check" type="checkbox" checked={on} onChange={() => toggle(f.value)} />
              </label>
            );
          })}
        </div>
        {formats.includes('objections') && !transcribed ? (
          <span className="zy-field__aide">Les citations rejoignent aussi l’étude comme verbatims : elles pourront prouver des insights.</span>
        ) : null}
      </div>

      <label className="zy-field">
        <span className="zy-field__label">Ton</span>
        <Select value={tone} onChange={(e) => setTone(e.target.value as InterviewTone)}>
          {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
        <span className="zy-field__aide">S’applique aux synthèses ; les citations restent mot pour mot.</span>
      </label>

      {!importer.generate && formats.length > 0 ? (
        <Note tone="lecture">La génération par l’IA n’est pas configurée sur ce serveur.</Note>
      ) : null}
      {run.error ? <ErrorBanner>{errorOf(run.error)}</ErrorBanner> : null}
      {done ? <p className="zy-texte-muet" role="status">{done}</p> : null}
      <Button variant="accent" className="itw-go" loading={run.isPending} disabled={!ready} onClick={() => void generate()}>
        {formats.length === 0 ? 'Importer la transcription' : 'Générer mon contenu'}
      </Button>
      <p className="itw-tip">💡 Astuce : pour un long entretien, envoyez le fichier audio seul (M4A, MP3), plus rapide et plus léger.</p>
      </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// SYNTHÈSES
// ---------------------------------------------------------------------------

export function InterviewOutputs({ outputs }: { outputs: InterviewOutput[] }) {
  if (outputs.length === 0) {
    return (
      <section className="test-block">
        <h2 className="t-h3">Synthèses</h2>
        <p className="card-note">
          Rien de généré pour l’instant.{' '}
          <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => openTab('entretien', 'importer')}>
            Importer l’entretien et générer
          </Button>
        </p>
      </section>
    );
  }
  return (
    <>
      {outputs.map((o) => {
        const f = formatOf(o.format);
        return (
          <section key={o.id} className="test-block itw-output">
            <div className="block-head">
              <h2 className="t-h3 itw-output-title"><Icon name={f.icon} size={18} /> {f.title}</h2>
              <CopyButton text={o.content} />
            </div>
            <p className="zy-texte-muet">
              Ton {TONES.find((t) => t.value === o.tone)?.label.toLowerCase() ?? o.tone} · généré le{' '}
              {new Date(o.created_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
            <div className="transcript">{o.content}</div>
          </section>
        );
      })}
    </>
  );
}
